/**
 * Visualization
 * @author David <david.prenninger@gmx.de>
 *
 * This file provides the application store for all values related to the visualization.
 */

import { generateDimensions } from '@/helpers/dimensions';

export default {
  state: {
    tableId: '',
    dimensions: {},
    objects: [],
    objectSections: [],
    arrow: {},
    offset: { x: 200 / 500, y: 25 / 500 },
    finish: false,
  },
  mutations: {
    // TABLE DIMENSIONS
    SET_DIMENSIONS(currentState, payload) {
      currentState.dimensions = payload;
      if (payload[0].raisedWidth && payload[0].raisedWidth !== 0) {
        currentState.offset = { x: payload[0].raisedWidth / 2, y: payload[0].raisedWidth / 2 };
      }
    },
    SET_HEIGHT(currentState, payload) {
      currentState.dimensions = currentState.dimensions.map(
        (dimension) => ({ ...dimension, z: payload }),
      );
    },
    SET_RAISED_HEIGHT(currentState, payload) {
      currentState.dimensions = currentState.dimensions.map(
        (dimension) => ({ ...dimension, raisedHeight: Number(payload) }),
      );
    },
    SET_RAISED_WIDTH(currentState, payload) {
      currentState.dimensions = currentState.dimensions.map(
        (dimension) => ({ ...dimension, raisedWidth: Number(payload) }),
      );
      currentState.offset = { x: payload / 2, y: payload / 2 };
    },
    SET_SIDE(currentState, payload) {
      currentState.dimensions = currentState.dimensions.map((dimension, index) => {
        if (payload.index === index) {
          return { ...dimension, ...payload.data };
        }
        return dimension;
      });
      if (payload.data.x && payload.index === 0 && payload.data.x < 400 / 500) {
        currentState.offset = { x: payload.data.x / 2, y: payload.data.x / 2 };
      }
    },
    // Objects
    SET_OBJECT(currentState, payload) {
      currentState.objects = payload;
    },
    ADD_OBJECT(currentState, payload) {
      currentState.objects.push(payload);
    },
    UPDATE_OBJECT(currentState, payload) {
      currentState.objects = currentState.objects.map((object, index) => {
        if (index === payload.index) {
          return payload.object;
        }
        return object;
      });
    },
    REMOVE_OBJECT(currentState) {
      currentState.objects.pop();
    },

    // OFFSET
    SET_OFFSET(currentState, payload) {
      currentState.offset = { ...currentState.offset, ...payload };
    },

    // ARROW
    SET_ARROW(currentState, payload) {
      currentState.arrow = payload;
    },


    // SECTIONS
    SECTIONS_SET_OBJECT(currentState, payload) {
      if (typeof payload.index !== 'undefined') {
        currentState.objectSections = currentState.objectSections.map((section, index) => {
          if (index === payload.index) {
            return { ...section, ...payload.data };
          }
          return section;
        });
      } else {
        let objectKey;
        if (payload.data.connector) {
          objectKey = 'connector';
        } else {
          [objectKey] = Object.keys(payload.data);
        }

        if (currentState.objectSections.length === 0) {
          currentState.objectSections.push({ ...payload.data });
        } else if (currentState.objectSections[currentState.objectSections.length - 1][objectKey]) {
          currentState.objectSections.push({ ...payload.data });
        } else {
          currentState.objectSections = currentState.objectSections.map((section, index) => {
            if (index === currentState.objectSections.length - 1) {
              return { ...section, ...payload.data };
            }
            return section;
          });
        }
      }
    },
    SECTIONS_REMOVE_OBJECT(currentState) {
      if (currentState.objectSections[currentState.objectSections.length - 1].glass) {
        currentState.objectSections = currentState.objectSections.map((section, index) => {
          if (index === currentState.objectSections.length - 1) {
            return { ...section, glass: undefined };
          }
          return section;
        });
      } else {
        currentState.objectSections.pop();
      }
    },
    LOAD_SECTION_OBJECTS(currentState, payload) {
      currentState.objectSections = payload;
    },

    // FINISH
    SET_FINISH(currentState, payload) {
      currentState.finish = payload;
    },

    SET_TABLE_ID(currentState, payload) {
      currentState.tableId = payload;
    },
  },
  actions: {
    // TABLE
    setTable(context, payload) {
      if (payload) {
        context.commit('SET_DIMENSIONS', generateDimensions(payload));
        context.commit('SET_TABLE_ID', payload.id);
        context.commit('SET_FINISH', false);
      }
    },
    setHeight(context, payload) {
      context.commit('SET_HEIGHT', payload);
    },
    setRaisedHeight(context, payload) {
      context.commit('SET_RAISED_HEIGHT', payload);
    },
    setRaisedWidth(context, payload) {
      context.commit('SET_RAISED_WIDTH', payload);
    },
    setSide(context, payload) {
      context.commit('SET_SIDE', payload);
    },
    loadDimensions(context, payload) {
      context.commit('SET_DIMENSIONS', payload);
    },

    // OBJECTS
    addObject(context, payload) {
      context.commit('ADD_OBJECT', payload);
    },
    updateObject(context, payload) {
      context.commit('UPDATE_OBJECT', payload);
    },
    removeObject(context) {
      context.commit('REMOVE_OBJECT');
    },
    setObjects(context, payload) {
      context.commit('SET_OBJECT', payload);
    },

    // OFFSET
    setOffset(context, payload) {
      context.commit('SET_OFFSET', payload);
    },
    loadOffset(context, payload) {
      context.commit('SET_OFFSET', payload);
    },

    // ARROWS
    setArrow(context, payload) {
      context.commit('SET_ARROW', payload);
    },

    // OBJECT SECTIONS
    setSectionObject(context, payload) {
      context.commit('SECTIONS_SET_OBJECT', payload);
    },
    removeLastSectionObject(context) {
      context.commit('SECTIONS_REMOVE_OBJECT');
    },
    loadSectionObjects(context, payload) {
      context.commit('LOAD_SECTION_OBJECTS', payload);
    },

    // Finish
    setFinish(context, payload) {
      context.commit('SET_FINISH', payload);
    },
  },
  getters: {
    objects(state) {
      return state.objects;
    },
    dimensions(state) {
      return state.dimensions;
    },
    offset(state) {
      return state.offset;
    },
    arrow(state) {
      return state.arrow;
    },
    objectSections(state) {
      return state.objectSections;
    },
    finish(state) {
      return state.finish;
    },
    tableId(state) {
      return state.tableId;
    },
  },
};
